// KPI Builder — funnels. A funnel does not query data itself: it connects existing KPIs, one per step.
// Shape of the live “In-Service Enrollment Funnel” (kpistagefunnel) and “Registration Conversion Funnel” (conversionfunnel).
import { INDEX_PREFIX } from 'constants/kpiBuilderCatalogue';
import { FunnelAnswers, FunnelStage, QueryObject } from './types';

export const STAGE_COLORS = ['', 'stage-amber', 'stage-blue', 'stage-green'];

/** First step plain, last step green, the ones between take the other colours in turn. */
export function colorFor(index: number, total: number): string {
  if (index === 0) return '';
  if (index === total - 1) return STAGE_COLORS[3];
  return STAGE_COLORS[1 + ((index - 1) % 2)];
}

export const isFunnelComplete = (f: FunnelAnswers): boolean => {
  const codes = f.stages.map((s) => s.kpi);
  return f.stages.length >= 2 && codes.every(Boolean) && new Set(codes).size === codes.length && f.stages.every((s) => s.label.trim() !== '');
};

const keyOf = (label: string): string => {
  const words = label.replace(/[^A-Za-z0-9 ]/g, ' ').split(' ').filter(Boolean);
  return words.map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join('') || 'branch';
};

const stageConfig = (s: FunnelStage, i: number, all: FunnelStage[]): Record<string, unknown> => ({
  label: s.label,
  ...(s.gapKpi ? { gapLabel: s.gapLabel || 'Not yet' } : {}),
  stageKey: `stage${i + 1}`,
  colorToken: colorFor(i, all.length),
  ...(i > 0 ? { pillSuffix: s.pill || `of ${all[i - 1].label}` } : {}),
  visualizationCode: s.kpi,
  ...(s.gapKpi ? { gapVisualizationCode: s.gapKpi } : {}),
});

/** The funnel needs one query to exist; the dashboard reads the step values from the connected KPIs. */
export function placeholderQuery(label: string): QueryObject {
  return {
    module: 'COMMON', dataSource: 'ES', esInstance: 'QUADNARY', requestQueryMap: {}, dateRefField: 'registrationDate',
    indexName: INDEX_PREFIX + 'drishti-user-registration-data', aggrQuery: { size: 0, aggs: { [label]: { value_count: { field: 'userId' } } } },
  };
}

export function funnelConfig(f: FunnelAnswers): Record<string, unknown> {
  if (f.kind === 'stage') {
    return { stages: f.stages.map((s, i, all) => stageConfig(s, i, all)), insight: {}, valueType: 'number', aggregationPaths: [f.stages[0].label] };
  }
  const [root, ...branches] = f.stages;
  return {
    chartType: 'conversionfunnel',
    root: { visualizationCode: root.kpi, label: root.label, colorToken: '' },
    branches: branches.map((b, i) => ({ visualizationCode: b.kpi, label: b.label, branchKey: keyOf(b.label), colorToken: colorFor(i + 1, f.stages.length) })),
    insight: {},
  };
}
